import React, { useState } from "react";
import { useCancelEmailChange } from "../../hooks/user";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Snackbar, Alert, useTheme, useMediaQuery } from "@mui/material";
import { useError } from "../../error/useError";
import ErrorAlert from "../common/ErrorAlert";

type CancelEmailChangeDialogProps = {
  open: boolean;
  onClose: () => void;
  onCompleted?: () => void;
};

const CancelEmailChangeDialog: React.FC<CancelEmailChangeDialogProps> = ({ open, onClose, onCompleted }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { error, helpText, clearError, handleError } = useError();
  const [successOpen, setSuccessOpen] = useState(false);
  
  const { mutate: cancelEmailChange, isPending: isLoading } = useCancelEmailChange();

  const onConfirm = () => {
    clearError();
    
    cancelEmailChange(undefined, {
      onSuccess: () => {
        setSuccessOpen(true);
        onClose();
        if (onCompleted) onCompleted();
      },
      onError: (error) => {
        // Use the backend's user-friendly error message
        handleError(error);
      }
    });
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" fullScreen={fullScreen}>
        <DialogTitle sx={{ fontWeight: 700 }}>Cancel email change?</DialogTitle>
        <DialogContent dividers>
          <ErrorAlert error={error} helpText={helpText} onClear={clearError} title="Action failed" />
          <Typography variant="body2" color="text.secondary">
            This will invalidate pending verification links sent to both your current and new email addresses.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={isLoading}>Keep pending change</Button>
          <Button color="error" variant="contained" onClick={onConfirm} disabled={isLoading}>
            Cancel email change
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={successOpen} autoHideDuration={3000} onClose={() => setSuccessOpen(false)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert onClose={() => setSuccessOpen(false)} severity="success" variant="filled" sx={{ width: "100%" }}>
          Email change request canceled.
        </Alert>
      </Snackbar>
    </>
  );
};

export default CancelEmailChangeDialog;



